import ErrorMessage from "./ErrorMessage";
import { useFormikContext, getIn } from "formik";
import { Dropdown } from "primereact/dropdown";
import { useEffect, useState } from "react";
import { ApiResponse } from "../../services/base/apiResponse";
import { Button } from "primereact/button";
import AbstractEditDialog from "../../pages/nomenclatures/common/AbstractEditDialog";

export type CustomDropdownProps<T> = {
  label: string;
  id: string;
  options: T[];
  optionLabel: keyof T | ((option: T) => string);
  optionValue: keyof T;
  filterValue?: keyof T;
  filterField?: string;
  filterExpression?: (option: T) => boolean;
  labelIcon?: string;
  defaultValue?: T | ((options: T[]) => T | undefined);
  createDialog?: any;
  createItemMutation?: any;
  required?: boolean;
  [key: string]: any;
};

const CustomDropdown = <T extends object>({
  label,
  id,
  options,
  optionLabel,
  optionValue,
  filterValue,
  filterField,
  defaultValue,
  filterExpression,
  createDialog,
  createItemMutation,
  required,
  ...props
}: CustomDropdownProps<T>) => {
  const formik = useFormikContext<any>();

  const getOptionLabel = (option: T): string => {
    return typeof optionLabel === "function" ? optionLabel(option) : (option[optionLabel] as string);
  };

  const filteredOptions = options.filter(
    (x) =>
      (filterValue && filterField ? x[filterValue] === formik.values[filterField] : true) &&
      (!filterExpression || filterExpression(x)),
  );

  const flattenOptions = (options: T[], level: number = 0): T[] => {
    return options.reduce((acc, option) => {
      const flattenedOption = {
        ...option,
        _level: level,
        // Precompute label for function-based optionLabel to enable proper filtering
        _computedLabel: typeof optionLabel === "function" ? getOptionLabel(option) : undefined,
      };
      acc.push(flattenedOption);
      if (Array.isArray((option as any).children)) {
        acc.push(...flattenOptions((option as any).children, level + 1));
      }
      return acc;
    }, [] as T[]);
  };

  const flattenedOptions = flattenOptions(filteredOptions);

  useEffect(() => {
    if (defaultValue && getIn(formik.values, id) === undefined) {
      const value =
        typeof defaultValue === "function" ? (defaultValue as (options: T[]) => T | undefined)(options) : defaultValue;
      if (value) {
        formik.setFieldValue(id, value[optionValue]);
      }
    }
  }, [defaultValue, formik, id, options]);

  const [createDialogState, setCreateDialogState] = useState<{
    open: boolean;
    item: T | null;
  }>({
    open: false,
    item: createDialog?.item ?? null,
  });

  const openCreateDialog = () => {
    setCreateDialogState({ open: true, item: createDialog?.item ?? null });
  };

  const closeCreateDialog = () => {
    setCreateDialogState({ open: false, item: null });
  };

  const handleCreateDialogSubmit = (values: ApiResponse<Partial<T>>) => {
    if (values?.data) {
      formik.setFieldValue(id, values.data[optionValue as keyof T]);
      //options.push(values.data as T);
      closeCreateDialog();
    }
  };

  return (
    <div className="flex flex-column gap-2 mb-2">
      <label htmlFor={`dd-${id}`}>
        {label}
        {props.labelIcon && <i className={`${props.labelIcon} text-yellow-300 ml-2`} />}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="flex flex-row">
        <Dropdown
          id={id}
          inputId={`dd-${id}`}
          value={getIn(formik.values, id)}
          onChange={(e) => formik.setFieldValue(id, e.value)}
          onBlur={formik.handleBlur}
          options={flattenedOptions}
          appendTo={typeof document !== "undefined" ? document.body : undefined}
          placeholder={props.placeholder}
          itemTemplate={(option) => (
            <div style={{ paddingLeft: `${option._level * 20}px`, fontWeight: option._level > 0 ? "bold" : "normal" }}>
              <span>{getOptionLabel(option)}</span>
            </div>
          )}
          optionLabel={typeof optionLabel === "function" ? "_computedLabel" : (optionLabel as string)}
          optionValue={optionValue as string}
          invalid={getIn(formik.touched, id) && !!getIn(formik.errors, id)}
          className="w-full"
          filterBy={typeof optionLabel === "function" ? "_computedLabel" : props.filterBy}
          {...props}
        />
        {createDialog && <Button type="button" icon="pi pi-plus" onClick={openCreateDialog} />}
      </div>
      <ErrorMessage id={id} formik={formik} />

      {createDialogState.open && createDialog && (
        <AbstractEditDialog
          item={createDialogState.item}
          onDialogClose={closeCreateDialog}
          onDialogSubmit={handleCreateDialogSubmit}
          updateMutation={createItemMutation}
          validationSchema={createDialog.validationSchema}
          renderForm={(formik) => createDialog.renderForm(formik)}
        />
      )}
    </div>
  );
};

export default CustomDropdown;
